"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createReconstructor = createReconstructor;
const solve_1 = require("./solve");
const trainer_1 = require("./trainer");
const types_1 = require("./types");
/** v1 asserts flightTime > 0.01 s: any shorter and the seed divides by almost nothing. */
const MIN_SECONDS = 0.01;
/** Bounds each velocity component, m/s; far beyond any stroke, there only to keep the solver finite. */
const MAX_COMPONENT = 150;
const MAX_ITERATIONS = 20;
const toDeg = (rad) => (rad * 180) / Math.PI;
const finitePoint = (p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);
const invalid = (message) => ({ ok: false, reason: "invalidInput", message });
function createReconstructor(profile, kernel, options) {
    const { ball, environment } = profile;
    const { gravity } = environment;
    const dragPerMass = (0.5 * environment.airDensity * ball.dragCoefficient * Math.PI * (ball.diameter / 2) ** 2) / ball.mass;
    const { spinModel } = kernel;
    // Finite-difference step: the rk4 free flight is smooth to rounding; the
    // legacy scheme is noisier, as in aim.
    const fdStep = kernel.integrator === "legacy" ? 1e-3 : 1e-5;
    const state = new Float64Array(8);
    /**
     * The free-flight position at `seconds` into out[1..3]. rk4 stops exactly
     * there; the legacy scheme only samples every step, so it reads the
     * flight's interpolated path instead (and allocates it, being the
     * comparison mode rather than the hot path).
     */
    const positionAt = (release, seconds, out) => {
        if (kernel.integrator === "rk4")
            return kernel.endpoint(release, { time: seconds }, out, "none");
        const flight = kernel.fly(release, { until: { kind: "time", seconds }, surfaces: "none", path: true });
        if (flight.stoppedBy !== "until" || flight.path === undefined)
            return false;
        const p = flight.path.at(seconds);
        out[1] = p.x;
        out[2] = p.y;
        out[3] = p.z;
        return true;
    };
    return function reconstruct(strike, at, seconds, spin = types_1.NO_SPIN) {
        if (!finitePoint(strike) || !finitePoint(at))
            return invalid("strike and observed points must be finite");
        if (!(seconds > MIN_SECONDS && Number.isFinite(seconds))) {
            return invalid(`seconds must be greater than ${MIN_SECONDS}, got ${seconds}`);
        }
        if (!Number.isFinite(spin.rpm) || !Number.isFinite(spin.axis))
            return invalid("spin must be finite");
        const seed = idealVelocity(strike, at, seconds, gravity);
        // v1's stroke axis is fixed for a ball flying +y, so v1 negates the
        // spin of a stroke the seed sends toward -y. The physical model turns
        // the axis with the stroke instead and needs no negation.
        const strokeSpin = spinModel === "v1" && seed.y < 0 ? { rpm: -spin.rpm, axis: spin.axis } : spin;
        const releaseFor = (u) => (0, trainer_1.strokeRelease)(strike, { x: u[0], y: u[1], z: u[2] }, strokeSpin, spinModel);
        const solution = (0, solve_1.solve)((u, r) => {
            if (!positionAt(releaseFor(u), seconds, state))
                return false;
            r[0] = state[1] - at.x;
            r[1] = state[2] - at.y;
            r[2] = state[3] - at.z;
            return true;
        }, [seed.x, seed.y, seed.z], {
            lower: [-MAX_COMPONENT, -MAX_COMPONENT, -MAX_COMPONENT],
            upper: [MAX_COMPONENT, MAX_COMPONENT, MAX_COMPONENT],
            steps: [fdStep, fdStep, fdStep],
            maxIterations: MAX_ITERATIONS,
            done: (r) => Math.hypot(r[0], r[1], r[2]) <= options.tolerance,
            jacobian: seedJacobian(seed, seconds, dragPerMass),
        });
        if (!solution.defined)
            return invalid(`the flight cannot be followed for ${seconds} s`);
        const fit = {
            ...strikeAngles({ x: solution.u[0], y: solution.u[1], z: solution.u[2] }),
            flight: kernel.fly(releaseFor(solution.u), { stopOutOfBounds: false }),
            miss: Math.hypot(solution.r[0], solution.r[1], solution.r[2]),
            iterations: solution.iterations,
        };
        return solution.converged
            ? { ok: true, ...fit }
            : {
                ok: false,
                reason: "noConvergence",
                message: `missed by ${fit.miss.toFixed(3)} m after ${fit.iterations} iterations`,
                closest: fit,
            };
    };
}
/** v1 _getInitialVelocityGuess: constant horizontal velocity, gravity only. */
function idealVelocity(strike, at, seconds, gravity) {
    return {
        x: (at.x - strike.x) / seconds,
        y: (at.y - strike.y) / seconds,
        z: (at.z - strike.z) / seconds + 0.5 * gravity * seconds,
    };
}
/**
 * How the position at `seconds` moves with the strike velocity, near enough
 * to start Newton without differencing (which costs three flights). With
 * linear drag k it is (1 - e^(-kt)) / k in each direction; quadratic drag is
 * taken as linear at the seed's speed. Broyden updates correct the rest.
 */
function seedJacobian(seed, seconds, dragPerMass) {
    const k = dragPerMass * Math.hypot(seed.x, seed.y, seed.z);
    const d = k > 0 ? -Math.expm1(-k * seconds) / k : seconds;
    return [
        [d, 0, 0],
        [0, d, 0],
        [0, 0, d],
    ];
}
/** Speed and direction of a strike velocity, as v1 reports launchSpeed, launchPitch and launchYaw. */
function strikeAngles(velocity) {
    const { x, y, z } = velocity;
    return {
        velocity,
        speed: Math.sqrt(x * x + y * y + z * z),
        pitch: toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))),
        yaw: toDeg(Math.atan2(x, y)),
    };
}
